import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, User, LogOut, LayoutDashboard, Shield, Bell, ChevronDown, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from './ui/dropdown-menu';
import { useAuth, API } from '../App';
import { NextFlipLogo, NextFlipIcon } from './Logo';
import { SupportButton } from './SupportButton';
import axios from 'axios';

export const Navbar = () => {
  const location = useLocation();
  const { user, token, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);

  const navLinks = [
    { href: '/marketplace', label: 'Deals' },
    { href: '/directory', label: 'Directory' },
    { href: '/post-deal', label: 'Post a Deal' }
  ];

  const isActive = (path) => location.pathname === path;

  // Fetch unread inquiry count for notification badge
  useEffect(() => {
    if (token && user) {
      fetchUnreadCount();
      fetchUnreadMessages();
      // Poll every 30 seconds for new inquiries and messages
      const interval = setInterval(() => {
        fetchUnreadCount();
        fetchUnreadMessages();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [token, user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/inquiries/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      // Silently fail
    }
  };

  const fetchUnreadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessages(response.data.unread_count || 0);
    } catch (error) {
      // Silently fail
    }
  };

  return (
    <header className="sticky top-0 z-50 glass-header" data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo">
            <NextFlipLogo size="default" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'bg-sky-50 text-[#5DADE2]'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* For... Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors" data-testid="nav-for-dropdown">
                  For...
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/for-investors" className="cursor-pointer">
                    For Investors
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/for-agents" className="cursor-pointer">
                    For Agents & Brokers
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/for-wholesalers" className="cursor-pointer">
                    For Wholesalers
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/for-providers" className="cursor-pointer">
                    For Service Providers
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                {/* Messages Icon */}
                <Link to="/dashboard?tab=messages" className="relative p-2 rounded-full hover:bg-slate-100" data-testid="messages-icon">
                  <MessageCircle className="h-5 w-5 text-slate-600" />
                  {unreadMessages > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#5DADE2] text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="messages-badge">
                      {unreadMessages > 9 ? '9+' : unreadMessages}
                    </span>
                  )}
                </Link>

                {/* Notification Bell */}
                <Link to="/dashboard?tab=inquiries" className="relative p-2 rounded-full hover:bg-slate-100" data-testid="notification-bell">
                  <Bell className="h-5 w-5 text-slate-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Link>

                {/* Support Button */}
                <div className="p-2">
                  <SupportButton variant="navbar" />
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 rounded-full" data-testid="user-menu-btn">
                      <div className="w-8 h-8 bg-sky-100 rounded-full flex items-center justify-center">
                        {user.picture ? (
                          <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <span className="text-[#5DADE2] font-medium text-sm">{user.name?.charAt(0)}</span>
                        )}
                      </div>
                      <span className="font-medium text-slate-900">{user.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="text-sm font-medium text-slate-900">{user.name}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="flex items-center gap-2">
                        <LayoutDashboard className="h-4 w-4" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard?tab=inquiries" className="flex items-center gap-2">
                        <Bell className="h-4 w-4" />
                        Inquiries
                        {unreadCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">{unreadCount}</span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard?tab=messages" className="flex items-center gap-2">
                        <MessageCircle className="h-4 w-4" />
                        Messages
                        {unreadMessages > 0 && (
                          <span className="ml-auto bg-[#5DADE2] text-white text-xs px-1.5 py-0.5 rounded-full">{unreadMessages}</span>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2">
                          <Shield className="h-4 w-4" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="text-red-600 focus:text-red-600">
                      <LogOut className="h-4 w-4 mr-2" />
                      Log Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" className="rounded-full" data-testid="nav-login-btn">
                  <Link to="/login">Log In</Link>
                </Button>
                <Button asChild className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full" data-testid="nav-signup-btn">
                  <Link to="/signup">Sign Up Free</Link>
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-slate-600 hover:text-slate-900"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-2">
              {navLinks.map(link => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium ${
                    isActive(link.href)
                      ? 'bg-sky-50 text-[#5DADE2]'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* For... Links in Mobile */}
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">For...</p>
              </div>
              <Link
                to="/for-investors"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                For Investors
              </Link>
              <Link
                to="/for-agents"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                For Agents & Brokers
              </Link>
              <Link
                to="/for-wholesalers"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                For Wholesalers
              </Link>
              <Link
                to="/for-providers"
                onClick={() => setMobileMenuOpen(false)}
                className="px-4 py-3 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                For Service Providers
              </Link>
              
              <div className="border-t border-slate-200 pt-4 mt-2">
                {user ? (
                  <>
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-100"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Link>
                    {user.role === 'admin' && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-100"
                      >
                        <Shield className="h-4 w-4" />
                        Admin Panel
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="flex items-center gap-2 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 w-full text-left"
                    >
                      <LogOut className="h-4 w-4" />
                      Log Out
                    </button>
                  </>
                ) : (
                  <div className="flex gap-3 px-4">
                    <Button asChild variant="outline" className="flex-1 rounded-full">
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)}>Log In</Link>
                    </Button>
                    <Button asChild className="flex-1 bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full">
                      <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>Sign Up Free</Link>
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
